import { useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Drop, Wrench, Question, Plus, Warning, Note,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const KINDS = [
  { key: "ran_out", label: "Ran out of something", icon: Drop, color: "#E07A5F" },
  { key: "broken_tool", label: "Tool broke", icon: Wrench, color: "#C14953" },
  { key: "new_issue", label: "Found new issue", icon: Question, color: "#2C4251" },
  { key: "extra_material", label: "Need extra material", icon: Plus, color: "#E07A5F" },
  { key: "step_blocked", label: "Step is blocked", icon: Warning, color: "#C14953" },
  { key: "note", label: "Just a note", icon: Note, color: "#594A42" },
];

export default function AddUpdateSheet({ open, onOpenChange, projectId, steps, onSaved }) {
  const [kind, setKind] = useState(null);
  const [note, setNote] = useState("");
  const [materialName, setMaterialName] = useState("");
  const [materialQty, setMaterialQty] = useState("");
  const [stepTitle, setStepTitle] = useState("");
  const [affectedStepId, setAffectedStepId] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setKind(null);
    setNote("");
    setMaterialName("");
    setMaterialQty("");
    setStepTitle("");
    setAffectedStepId("");
  };

  const submit = async () => {
    if (!kind) return;
    const body = { kind, note };
    if (kind === "ran_out" || kind === "extra_material") {
      if (!materialName.trim()) {
        toast.error("Add a material name");
        return;
      }
      body.new_materials = [{
        name: materialName.trim(),
        quantity: materialQty.trim(),
        status: "need_to_buy",
      }];
    }
    if (kind === "broken_tool") {
      if (!materialName.trim()) {
        toast.error("Add the replacement item name");
        return;
      }
      body.new_materials = [{
        name: materialName.trim(),
        quantity: materialQty.trim(),
        status: "need_to_buy",
      }];
      body.affected_step_id = affectedStepId || (steps?.find((s) => s.status === "current")?.id);
    }
    if (kind === "new_issue") {
      if (!stepTitle.trim()) {
        toast.error("Add a step title");
        return;
      }
      body.new_steps = [{ title: stepTitle.trim() }];
    }
    if (kind === "step_blocked") {
      if (!affectedStepId) {
        toast.error("Pick the step that's blocked");
        return;
      }
      body.affected_step_id = affectedStepId;
    }
    try {
      setBusy(true);
      const r = await api.post(`/projects/${projectId}/updates`, body);
      toast.success("Project updated");
      onSaved?.(r.data.project);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <SheetContent
        side="bottom"
        data-testid="add-update-sheet"
        className="bg-chalk border-t-2 border-stone_border rounded-t-3xl p-0 max-h-[88vh] overflow-y-auto"
      >
        <div className="mx-auto w-12 h-1.5 bg-stone_border rounded-full my-3" />
        <SheetHeader className="px-6">
          <SheetTitle className="font-heading text-2xl text-left">
            {kind ? "Add update" : "What happened?"}
          </SheetTitle>
          <SheetDescription className="text-left text-sm text-timber-700">
            {kind
              ? "Add a few details and we'll update the project."
              : "Pick what changed — we'll update steps and materials for you."}
          </SheetDescription>
        </SheetHeader>
        <div className="px-6 py-4 pb-8 max-w-md mx-auto w-full">
          {!kind ? (
            <div className="grid grid-cols-2 gap-3">
              {KINDS.map(({ key, label, icon: Icon, color }) => (
                <button
                  key={key}
                  data-testid={`update-chip-${key}`}
                  onClick={() => setKind(key)}
                  className="tactile-card btn-press p-4 text-left flex flex-col gap-2 hover:shadow-tactile-orange"
                >
                  <Icon size={28} color={color} weight="duotone" />
                  <span className="text-sm font-bold text-timber-900 leading-tight">{label}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {(kind === "ran_out" || kind === "extra_material" || kind === "broken_tool") && (
                <>
                  <div>
                    <Label className="label-cap">
                      {kind === "broken_tool" ? "Replacement item" : "Item name"}
                    </Label>
                    <Input
                      data-testid="update-material-name"
                      value={materialName}
                      onChange={(e) => setMaterialName(e.target.value)}
                      placeholder={kind === "broken_tool" ? "e.g. Drill bit set" : "e.g. Paint, 2L"}
                      className="min-h-[56px] mt-1 bg-chalk border-2 border-stone_border rounded-xl text-lg"
                    />
                  </div>
                  <div>
                    <Label className="label-cap">Quantity (optional)</Label>
                    <Input
                      data-testid="update-material-qty"
                      value={materialQty}
                      onChange={(e) => setMaterialQty(e.target.value)}
                      placeholder="e.g. 1 tin"
                      className="min-h-[56px] mt-1 bg-chalk border-2 border-stone_border rounded-xl text-lg"
                    />
                  </div>
                </>
              )}
              {kind === "new_issue" && (
                <div>
                  <Label className="label-cap">New step</Label>
                  <Input
                    data-testid="update-step-title"
                    value={stepTitle}
                    onChange={(e) => setStepTitle(e.target.value)}
                    placeholder="e.g. Patch new crack near skirting"
                    className="min-h-[56px] mt-1 bg-chalk border-2 border-stone_border rounded-xl text-lg"
                  />
                </div>
              )}
              {(kind === "step_blocked" || kind === "broken_tool") && (
                <div>
                  <Label className="label-cap">Which step?</Label>
                  <select
                    data-testid="update-step-select"
                    value={affectedStepId}
                    onChange={(e) => setAffectedStepId(e.target.value)}
                    className="w-full min-h-[56px] mt-1 bg-chalk border-2 border-stone_border rounded-xl px-3 text-lg"
                  >
                    <option value="">
                      {kind === "broken_tool" ? "Current step (auto)" : "Pick step…"}
                    </option>
                    {(steps || []).map((s) => (
                      <option key={s.id} value={s.id}>{s.title}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label className="label-cap">Note (optional)</Label>
                <Textarea
                  data-testid="update-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Any extra detail…"
                  rows={3}
                  className="mt-1 bg-chalk border-2 border-stone_border rounded-xl text-base"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  data-testid="update-back"
                  variant="outline"
                  onClick={reset}
                  className="border-2 border-stone_border min-h-[56px] flex-1"
                >
                  Back
                </Button>
                <Button
                  data-testid="update-save"
                  onClick={submit}
                  disabled={busy}
                  className="bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] font-bold flex-[2]"
                >
                  {busy ? "Saving…" : "Save update"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
